import React, { Fragment } from "react";

import { coalesce, maybeGetClosureFor } from "@kazoohr/helpers";

import { NestedTableRow } from "./NestedTableRow";
import { Alignment, CheckState, TableColumn, TableDataType } from "./types";

interface NestedRowsGeneratorProps {
  alignment?: Alignment;
  checkState?: CheckState;
  columns?: TableColumn[];
  dataItem: TableDataType;
  dataKey: (item: TableDataType) => number | string;
  defaultAlignment?: string;
  expandedRowIds: Array<number | string>;
  getCheckState: (item: TableDataType) => CheckState;
  getChildrenIds: (item: TableDataType) => Array<number | string>;
  hasChildren?: boolean;
  nestedField: string;
  nestedLevel?: number;
  onToggleExpansionOne?: (id: any) => void;
  onToggleSelectOne?: (id: any) => void;
  showCheckboxes?: boolean;
}

export function NestedRowsGenerator(props: NestedRowsGeneratorProps) {
  const {
    dataItem,
    nestedLevel,
    expandedRowIds,
    nestedField,
    getChildrenIds,
    onToggleSelectOne,
    dataKey,
    columns = [],
    alignment,
    showCheckboxes,
    onToggleExpansionOne,
    getCheckState,
  } = props;
  const childrenRows = dataItem[nestedField]?.map((child: TableDataType) => {
    const childId = dataKey(child);
    if (expandedRowIds.includes(childId)) {
      return (
        <NestedRowsGenerator
          {...props}
          key={`generator-${childId}`}
          dataItem={child}
          nestedLevel={coalesce(nestedLevel, 0) + 1}
        />
      );
    }
    return (
      <NestedTableRow
        key={childId}
        columns={columns}
        dataItem={child}
        defaultAlignment={alignment}
        nestedLevel={coalesce(nestedLevel, 0) + 1}
        onToggleSelectOne={maybeGetClosureFor(onToggleSelectOne, childId)}
        showCheckboxes={showCheckboxes}
        onToggleExpansionOne={onToggleExpansionOne}
        hasChildren={!!(child[nestedField] || []).length}
        isExpanded={expandedRowIds.includes(childId)}
        checkState={getCheckState(child)}
        dataKey={dataKey}
      />
    );
  });
  const id = dataKey(dataItem);
  const childRowsIds = getChildrenIds(dataItem);
  return (
    <Fragment>
      <NestedTableRow
        key={id}
        columns={columns}
        dataItem={dataItem}
        defaultAlignment={alignment}
        nestedLevel={nestedLevel}
        onToggleSelectOne={
          // istanbul ignore next - simple closure
          () => {
            childRowsIds.forEach((childId) => onToggleSelectOne?.(childId));
            onToggleSelectOne?.(id);
          }
        }
        showCheckboxes={showCheckboxes}
        onToggleExpansionOne={onToggleExpansionOne}
        hasChildren={Boolean(dataItem?.[nestedField]?.length)}
        isExpanded={expandedRowIds.includes(id)}
        checkState={getCheckState(dataItem)}
        dataKey={dataKey}
      />
      {childrenRows}
    </Fragment>
  );
}
